import apiClient from './client';
import type {
  TaskWithDetails,
  TaskDetail,
  TaskStats,
  TaskActivityLog,
  TaskComment,
  TaskFilters,
  CreateTaskRequest,
  UpdateTaskRequest,
  BulkTaskRequest,
  TaskStatus,
  Milestone,
  CrossCohortTaskRow,
  CrossCohortTaskFilters,
  TaskAssignee,
  TaskGroupByField,
  TaskGroupCount,
  PaginatedResponse,
} from '../../types';

/**
 * Build query string from task filters
 */
function buildTaskQueryString(filters?: TaskFilters): string {
  if (!filters) return '';

  const params = new URLSearchParams();

  if (filters.status && filters.status.length > 0) {
    params.set('status', filters.status.join(','));
  }
  if (filters.phase && filters.phase.length > 0) {
    params.set('phase', filters.phase.join(','));
  }
  if (filters.assignee_id) {
    params.set('assignee_id', filters.assignee_id);
  }
  if (filters.session_id) {
    params.set('session_id', filters.session_id);
  }
  if (filters.include_subtasks) {
    params.set('include_subtasks', 'true');
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Build shared URLSearchParams from cross-cohort task filters (excludes sort/pagination)
 */
function buildCrossCohortFilterParams(filters?: CrossCohortTaskFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters?.search) params.set('search', filters.search);
  if (filters?.status && filters.status.length > 0) params.set('status', filters.status.join(','));
  if (filters?.phase && filters.phase.length > 0) params.set('phase', filters.phase.join(','));
  if (filters?.cohortIds && filters.cohortIds.length > 0) params.set('cohortIds', filters.cohortIds.join(','));
  if (filters?.assigneeIds && filters.assigneeIds.length > 0) params.set('assigneeIds', filters.assigneeIds.join(','));
  if (filters?.dueDateFrom) params.set('dueDateFrom', filters.dueDateFrom);
  if (filters?.dueDateTo) params.set('dueDateTo', filters.dueDateTo);
  if (filters?.isOverdue) params.set('isOverdue', 'true');
  if (filters?.includeOverdue) params.set('includeOverdue', 'true');
  if (filters?.flatten) params.set('flatten', 'true');
  return params;
}

/**
 * API for managing tasks
 */
export const tasksApi = {
  /**
   * Get all tasks for a cohort with optional filtering
   */
  getTasksForCohort: async (
    cohortId: string,
    filters?: TaskFilters,
  ): Promise<TaskWithDetails[]> => {
    const queryString = buildTaskQueryString(filters);
    return apiClient.get<TaskWithDetails[]>(`/api/cohorts/${cohortId}/tasks${queryString}`);
  },

  /**
   * Get task statistics for a cohort
   */
  getTaskStats: async (cohortId: string): Promise<TaskStats> => {
    return apiClient.get<TaskStats>(`/api/cohorts/${cohortId}/tasks/stats`);
  },

  /**
   * Get a single task with full details including subtasks and comments
   */
  getTaskById: async (cohortId: string, taskId: string): Promise<TaskDetail> => {
    return apiClient.get<TaskDetail>(`/api/cohorts/${cohortId}/tasks/${taskId}`);
  },

  /**
   * Create a new task
   */
  createTask: async (
    cohortId: string,
    data: CreateTaskRequest,
  ): Promise<TaskWithDetails> => {
    return apiClient.post<TaskWithDetails>(`/api/cohorts/${cohortId}/tasks`, data);
  },

  /**
   * Update a task
   */
  updateTask: async (
    cohortId: string,
    taskId: string,
    data: UpdateTaskRequest,
  ): Promise<TaskWithDetails> => {
    return apiClient.put<TaskWithDetails>(`/api/cohorts/${cohortId}/tasks/${taskId}`, data);
  },

  /**
   * Quick status update
   */
  updateTaskStatus: async (
    cohortId: string,
    taskId: string,
    status: TaskStatus,
    completeSubtasks?: boolean,
  ): Promise<TaskWithDetails> => {
    return apiClient.put<TaskWithDetails>(
      `/api/cohorts/${cohortId}/tasks/${taskId}/status`,
      { status, ...(completeSubtasks && { completeSubtasks }) },
    );
  },

  /**
   * Delete a task (soft delete)
   */
  deleteTask: async (cohortId: string, taskId: string): Promise<void> => {
    return apiClient.delete<void>(`/api/cohorts/${cohortId}/tasks/${taskId}`);
  },

  /**
   * Duplicate a task
   * Creates a copy with "Copy of " prefix, same description/priority/phase,
   * but resets status to not_started and clears due date/assignee
   */
  duplicateTask: async (cohortId: string, taskId: string): Promise<TaskWithDetails> => {
    return apiClient.post<TaskWithDetails>(
      `/api/cohorts/${cohortId}/tasks/${taskId}/duplicate`,
    );
  },

  /**
   * Get activity log for a task
   */
  getTaskActivity: async (
    cohortId: string,
    taskId: string,
    limit?: number,
  ): Promise<TaskActivityLog[]> => {
    const queryString = limit ? `?limit=${limit}` : '';
    return apiClient.get<TaskActivityLog[]>(
      `/api/cohorts/${cohortId}/tasks/${taskId}/activity${queryString}`,
    );
  },

  /**
   * Get comments for a task
   */
  getTaskComments: async (
    cohortId: string,
    taskId: string,
  ): Promise<TaskComment[]> => {
    return apiClient.get<TaskComment[]>(`/api/cohorts/${cohortId}/tasks/${taskId}/comments`);
  },

  /**
   * Add a comment to a task
   */
  addTaskComment: async (
    cohortId: string,
    taskId: string,
    content: string,
  ): Promise<TaskComment> => {
    return apiClient.post<TaskComment>(
      `/api/cohorts/${cohortId}/tasks/${taskId}/comments`,
      { content },
    );
  },

  /**
   * Perform bulk operations on tasks
   */
  bulkOperation: async (
    cohortId: string,
    request: BulkTaskRequest,
  ): Promise<{ affected_count: number }> => {
    return apiClient.post<{ affected_count: number }>(
      `/api/cohorts/${cohortId}/tasks/bulk`,
      request,
    );
  },

  /**
   * Get milestones for a cohort
   */
  getMilestones: async (cohortId: string): Promise<Milestone[]> => {
    return apiClient.get<Milestone[]>(`/api/cohorts/${cohortId}/milestones`);
  },

  /**
   * Get tasks assigned to the current user across all cohorts
   */
  getMyTasks: async (
    status?: TaskStatus[],
    limit?: number,
  ): Promise<TaskWithDetails[]> => {
    const params = new URLSearchParams();
    if (status && status.length > 0) {
      params.set('status', status.join(','));
    }
    if (limit) {
      params.set('limit', String(limit));
    }
    const queryString = params.toString();
    return apiClient.get<TaskWithDetails[]>(`/api/tasks/my-tasks${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get all tasks across all cohorts with filtering, sorting, and pagination
   */
  getAllTasks: async (
    filters?: CrossCohortTaskFilters,
  ): Promise<PaginatedResponse<CrossCohortTaskRow>> => {
    const params = buildCrossCohortFilterParams(filters);
    if (filters?.sortBy) params.set('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.set('sortOrder', filters.sortOrder);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));

    const queryString = params.toString();
    return apiClient.get<PaginatedResponse<CrossCohortTaskRow>>(
      `/api/tasks${queryString ? `?${queryString}` : ''}`,
    );
  },

  /**
   * Get group counts for cross-cohort tasks (lightweight — no task data)
   */
  getGroupCounts: async (
    filters: CrossCohortTaskFilters,
    groupBy: TaskGroupByField,
  ): Promise<TaskGroupCount[]> => {
    const params = buildCrossCohortFilterParams(filters);
    params.set('groupBy', groupBy);

    const queryString = params.toString();
    return apiClient.get<TaskGroupCount[]>(
      `/api/tasks/group-counts?${queryString}`,
    );
  },

  /**
   * Get users who have at least one task assigned (for filter avatars)
   */
  getTaskAssignees: async (): Promise<TaskAssignee[]> => {
    return apiClient.get<TaskAssignee[]>('/api/tasks/assignees');
  },
};

export default tasksApi;
